import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { CATEGORIES } from './constants';
import { EvidenceRows, AssessmentPicker, TagChips, TagPicker } from './shared';

export const EditObservationDialog = ({
  editObs, setEditObs, tags, tagsById, canManageEvidence, saving, onSave,
}) => (
  <>
    <Dialog open={!!editObs} onOpenChange={v => !v && setEditObs(null)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-obs-dialog">
        <DialogHeader><DialogTitle>Edit Observation</DialogTitle></DialogHeader>
        {editObs && (
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Timestamp</Label>
                <Input value={editObs.timestamp_display || ''}
                       onChange={e => setEditObs(o => ({ ...o, timestamp_display: e.target.value }))}
                       data-testid="edit-obs-ts" />
              </div>
              <div>
                <Label className="text-xs">Period / Part</Label>
                <Input value={editObs.period || ''}
                       onChange={e => setEditObs(o => ({ ...o, period: e.target.value }))}
                       data-testid="edit-obs-period" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={editObs.category} onValueChange={v => setEditObs(o => ({ ...o, category: v }))}>
                <SelectTrigger data-testid="edit-obs-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Development Tags</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => setEditObs(o => ({ ...o, _tagPicker: true }))} data-testid="edit-obs-open-tags">Select tags</Button>
              </div>
              <TagChips ids={editObs.development_tag_ids || []} tagsById={tagsById}
                        onRemove={id => setEditObs(o => ({ ...o, development_tag_ids: o.development_tag_ids.filter(x => x !== id) }))} />
            </div>
            <div>
              <Label className="text-xs">Assessment</Label>
              <AssessmentPicker value={editObs.assessment} onChange={v => setEditObs(o => ({ ...o, assessment: v }))} />
            </div>
            <div>
              <Label className="text-xs">Observation</Label>
              <Textarea rows={3} value={editObs.observation || ''}
                        onChange={e => setEditObs(o => ({ ...o, observation: e.target.value }))}
                        data-testid="edit-obs-text" />
            </div>
            <div>
              <Label className="text-xs">Evidence</Label>
              {!canManageEvidence && (editObs.evidence || []).length > 0 && (
                <p className="text-[11px] text-amber-600 mb-1">Removing or replacing existing evidence requires the Manage Evidence permission.</p>
              )}
              <EvidenceRows
                items={(editObs.evidence || []).map(e => ({
                  source: e.source, timestamp: e.timestamp_display || '', url: e.url || '',
                  clip_url: e.clip_url || '', label: e.label || '', id: e.id,
                  timestamp_seconds: e.timestamp_seconds, provider_ref: e.provider_ref,
                }))}
                setItems={items => setEditObs(o => ({ ...o, evidence: items.map(e => ({
                  id: e.id, source: e.source, timestamp_display: e.timestamp, url: e.url,
                  clip_url: e.clip_url, label: e.label, timestamp_seconds: e.timestamp_seconds,
                  provider_ref: e.provider_ref,
                })) }))}
                testidPrefix="edit-obs-evidence" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditObs(null)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="edit-obs-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {editObs?._tagPicker && (
      <TagPicker open onOpenChange={v => !v && setEditObs(o => ({ ...o, _tagPicker: false }))}
        tags={tags} selected={editObs.development_tag_ids || []}
        onToggle={id => setEditObs(o => ({
          ...o,
          development_tag_ids: o.development_tag_ids.includes(id)
            ? o.development_tag_ids.filter(x => x !== id) : [...o.development_tag_ids, id],
        }))} />
    )}
  </>
);

export default EditObservationDialog;
